// ============================================================
// theme.js
// Shared dashboard design tokens for the internal CNRF app.
// Exports both light and dark themes. The active theme is
// selected via ThemeContext at runtime.
// ============================================================

// Light theme — the original Schwab-style look
export const lightTheme = {
  bg: "#F3F6FA",
  surface0: "#0F4F78",
  cardBg: "#FFFFFF",
  surface2: "#EEF3F8",

  border: "#C9D5E2",
  borderSub: "#DCE5EE",

  shadow: {
    card: "0 10px 24px rgba(15, 42, 69, 0.06)",
    elevated: "0 18px 36px rgba(15, 42, 69, 0.1)",
    dropdown: "0 18px 40px rgba(15, 42, 69, 0.14)",
  },

  glow: {
    neutral: "0 10px 24px rgba(15, 42, 69, 0.06)",
    green: "0 10px 24px rgba(34, 138, 89, 0.12)",
    red: "0 10px 24px rgba(201, 76, 58, 0.12)",
    purple: "0 10px 24px rgba(106, 127, 198, 0.12)",
  },

  accent: "#0E72B8",
  accentLight: "#3F93CF",
  accentDim: "#D9ECF9",
  accentGlow: "#EAF4FB",

  accentStart: "#0E72B8",
  accentEnd: "#6A7FC6",

  positive: "#228A59",
  negative: "#C94C3A",
  warning: "#B8781B",
  gold: "#B28A3A",

  positiveDim: "#E3F4EA",
  negativeDim: "#FBE7E3",
  steelDim: "#E4EEF6",
  goldDim: "#F7EFD9",

  chartBlue: "#287DB8",
  chartPurple: "#6A7FC6",
  chartNavy: "#1E5F8E",

  textMain: "#12263A",
  textDim: "#526678",
  textFaint: "#8093A5",

  badgeSTO: "#228A59",
  badgeBTC: "#C94C3A",
  badgeBuy: "#287DB8",
  badgeSell: "#0E72B8",
  badgeRoll: "#6A7FC6",
  badgeDCF: "#71869A",
  badgeDIV: "#B8781B",
};

// Dark theme — same structure, dark palette
export const darkTheme = {
  bg: "#0d1117",
  surface0: "#0b3d5c",
  cardBg: "#161b22",
  surface2: "#1c2128",

  border: "#30363d",
  borderSub: "#21262d",

  shadow: {
    card: "0 10px 24px rgba(0, 0, 0, 0.25)",
    elevated: "0 18px 36px rgba(0, 0, 0, 0.35)",
    dropdown: "0 18px 40px rgba(0, 0, 0, 0.4)",
  },

  glow: {
    neutral: "0 10px 24px rgba(0, 0, 0, 0.2)",
    green: "0 10px 24px rgba(63, 185, 80, 0.15)",
    red: "0 10px 24px rgba(248, 81, 73, 0.15)",
    purple: "0 10px 24px rgba(130, 148, 196, 0.15)",
  },

  accent: "#58a6ff",
  accentLight: "#79b8ff",
  accentDim: "#1a3a52",
  accentGlow: "#0d2137",

  accentStart: "#58a6ff",
  accentEnd: "#8294c4",

  positive: "#3fb950",
  negative: "#f85149",
  warning: "#d29922",
  gold: "#c9a23a",

  positiveDim: "#1a2e1f",
  negativeDim: "#2e1a1a",
  steelDim: "#1a2433",
  goldDim: "#2a2418",

  chartBlue: "#58a6ff",
  chartPurple: "#8294c4",
  chartNavy: "#3d8fd4",

  textMain: "#e6edf3",
  textDim: "#8b949e",
  textFaint: "#6e7681",

  badgeSTO: "#3fb950",
  badgeBTC: "#f85149",
  badgeBuy: "#58a6ff",
  badgeSell: "#58a6ff",
  badgeRoll: "#8294c4",
  badgeDCF: "#8b949e",
  badgeDIV: "#d29922",
};

// Default export — light theme for backward compatibility
// Components that support theming should use ThemeContext instead
const cssVar = (name) => `var(${name})`;

export const theme = {
  bg: cssVar('--app-bg'),
  surface0: cssVar('--app-surface-0'),
  cardBg: cssVar('--app-card-bg'),
  surface2: cssVar('--app-surface-2'),
  border: cssVar('--app-border'),
  borderSub: cssVar('--app-border-sub'),
  shadow: {
    card: cssVar('--app-shadow-card'),
    elevated: cssVar('--app-shadow-elevated'),
    dropdown: cssVar('--app-shadow-dropdown'),
  },
  glow: {
    neutral: cssVar('--app-glow-neutral'),
    green: cssVar('--app-glow-green'),
    red: cssVar('--app-glow-red'),
    purple: cssVar('--app-glow-purple'),
  },
  accent: cssVar('--app-accent'),
  accentLight: cssVar('--app-accent-light'),
  accentDim: cssVar('--app-accent-dim'),
  accentGlow: cssVar('--app-accent-glow'),
  accentStart: cssVar('--app-accent-start'),
  accentEnd: cssVar('--app-accent-end'),
  positive: cssVar('--app-positive'),
  negative: cssVar('--app-negative'),
  warning: cssVar('--app-warning'),
  gold: cssVar('--app-gold'),
  positiveDim: cssVar('--app-positive-dim'),
  negativeDim: cssVar('--app-negative-dim'),
  steelDim: cssVar('--app-steel-dim'),
  goldDim: cssVar('--app-gold-dim'),
  chartBlue: cssVar('--app-chart-blue'),
  chartPurple: cssVar('--app-chart-purple'),
  chartNavy: cssVar('--app-chart-navy'),
  textMain: cssVar('--app-text-main'),
  textDim: cssVar('--app-text-dim'),
  textFaint: cssVar('--app-text-faint'),
  badgeSTO: cssVar('--app-badge-sto'),
  badgeBTC: cssVar('--app-badge-btc'),
  badgeBuy: cssVar('--app-badge-buy'),
  badgeSell: cssVar('--app-badge-sell'),
  badgeRoll: cssVar('--app-badge-roll'),
  badgeDCF: cssVar('--app-badge-dcf'),
  badgeDIV: cssVar('--app-badge-div'),
};

export const fonts = {
  main: "var(--font-main)",
  mono: "var(--font-mono)",
  display: "var(--font-display)",
};
